/**
 * Collection and search facets: filter panel, inline size/color/fit, sort.
 *
 * Document-level sort/kids handlers stay outside customElements.define so they
 * still run when SpeedKit (or another cache) registers an older class first.
 */
(() => {
  /**
   * Apply the current facet form without requiring the upgraded custom element.
   *
   * @param {{ closePanel?: boolean }} [options]
   * @returns {void}
   */
  const applyFacetsFromDocument = ({ closePanel = false } = {}) => {
    const facets = document.querySelector("custom-facets");

    if (facets && typeof facets.apply === "function") {
      facets.apply({ closePanel });
      return;
    }

    const form = document.querySelector("[data-facets-form]");

    if (form) {
      form.requestSubmit();
    }
  };

  /**
   * Write the selected sort option into the mobile drawer summary.
   *
   * @param {HTMLSelectElement} select
   * @returns {void}
   */
  const updateSortCurrentLabels = (select) => {
    const label = select.selectedOptions?.[0]?.textContent?.trim();

    if (!label) {
      return;
    }

    document.querySelectorAll("[data-sort-current]").forEach((element) => {
      element.textContent = label;
    });
  };

  /**
   * Bind sort-apply and kids-toggle listeners once per page.
   *
   * @returns {void}
   */
  const bindSharedFacetsEvents = () => {
    if (window.__groundiesFacetsDocEvents) {
      return;
    }

    window.__groundiesFacetsDocEvents = true;

    document.addEventListener(
      "change",
      (event) => {
        const target = event.target;

        if (!(target instanceof Element)) {
          return;
        }

        if (target.matches("[data-kids-sizes-toggle]")) {
          const sizeFilter = target.closest("[data-size-filter]");

          if (sizeFilter) {
            sizeFilter.classList.toggle("is-kids-open", target.checked);
            sizeFilter.open = true;
          }

          event.stopPropagation();
          return;
        }

        if (!target.matches("[data-sort-select]")) {
          return;
        }

        const value = target.value;

        document.querySelectorAll("[data-sort-select]").forEach((select) => {
          if (select.value !== value) {
            select.value = value;
          }
        });

        if (target instanceof HTMLSelectElement) {
          updateSortCurrentLabels(target);
        }

        const isMobileSort = Boolean(target.closest("[data-mobile-sort]"));
        const isDesktop = window.matchMedia("(min-width: 768px)").matches;

        if (isMobileSort && !isDesktop) {
          event.stopImmediatePropagation();
          return;
        }

        applyFacetsFromDocument({ closePanel: false });
        event.stopImmediatePropagation();
      },
      true
    );
  };

  bindSharedFacetsEvents();

  if (!customElements.get("facets-button")) {
    class FacetsButton extends HTMLElement {
      connectedCallback() {
        this.addEventListener("click", this.openFacets.bind(this));
      }

      openFacets() {
        const facetsWrapper = document.querySelector("custom-facets");
        if (!facetsWrapper || typeof facetsWrapper.toggle !== "function") {
          return;
        }

        facetsWrapper.toggle();
      }
    }

    customElements.define("facets-button", FacetsButton);
  }

  if (!customElements.get("custom-facets")) {
    class CustomFacets extends HTMLElement {
      static SELECTORS = {
        FILTER_INPUTS: ".facets__size-input, .facets__color-input, .facets__option-input",
        FILTER_OPTIONS: ".facets__size-option, .facets__color-option, .facets__option",
        COUNT_ELEMENTS: ".facets__size-count, .facets__option-count, .facets__color-count",
      };

      connectedCallback() {
        requestAnimationFrame(() => {
          this.init();
        });
      }

      init() {
        if (this.dataset.facetsReady === "true") {
          return;
        }

        this.dataset.facetsReady = "true";

        this.cacheElements();
        this.initializeState();
        this.setupEventListeners();
        this.setActiveFiltersFromUrl();
        this.syncKidsSizesToggle();

        const initialCount = Number(this.resultsCount?.dataset.count);

        if (Number.isFinite(initialCount)) {
          this.updateResultsCount(initialCount);
        }

        this.updateFilterCounts();
      }

      cacheElements() {
        this.panel = this.querySelector("[data-facets-panel]");
        this.inline = this.querySelector("[data-facets-inline]");
        this.backdrop = this.querySelector("[data-facets-backdrop]");
        this.form = this.querySelector("[data-facets-form]");
        this.openButton = this.querySelector("[data-facets-open]");
        this.closeButton = this.querySelector("[data-facets-close]");
        this.clearButton = this.querySelector("[data-facets-clear]");
        this.resultsCount = this.querySelector("[data-facets-results]");
        this.changesEl = this.querySelector("[data-facets-changes]");
        this.activeEl = this.querySelector("[data-facets-active]");
        this.loadingOverlay = this.querySelector(".facets__loading-overlay");
      }

      initializeState() {
        this.availabilityCache = new Map();
        this.isUpdating = false;
        this.pendingUpdate = null;
        this.DEBOUNCE_DELAY = 300;
        this.MAX_CACHE_SIZE = 20;
        this.initialChecked = this.getCheckedKeys();
        this.mediaQuery = window.matchMedia("(min-width: 768px)");
        this.syncSortSelects();
      }

      setupEventListeners() {
        if (this.openButton) {
          this.openButton.addEventListener("click", () => this.open());
        }
        if (this.closeButton) {
          this.closeButton.addEventListener("click", () => this.close());
        }

        if (this.backdrop) {
          this.backdrop.addEventListener("click", () => this.close());
        }

        if (this.form) {
          this.form.addEventListener("submit", (e) => this.handleSubmit(e));

          this.form.addEventListener("change", (e) => {
            if (e.target.matches("[data-kids-sizes-toggle], [data-sort-select]")) {
              return;
            }

            if (e.target.matches('input[type="checkbox"], select')) {
              this.handleFilterChange(e);
            }
          });

          this.form.addEventListener("click", (e) => {
            const tooltipTrigger = e.target.closest("[data-facets-tooltip]");

            if (tooltipTrigger) {
              e.preventDefault();
              e.stopPropagation();
              this.toggleFitTooltip(tooltipTrigger);
              return;
            }

            const chip = e.target.closest("[data-facets-remove]");

            if (chip) {
              e.preventDefault();
              e.stopPropagation();
              this.removeFilter(
                chip.getAttribute("data-name"),
                chip.getAttribute("data-value")
              );
            }
          });
        }

        this.querySelectorAll("[data-facets-clear]").forEach((button) => {
          button.addEventListener("click", () => this.clear());
        });

        this.handleMediaChange = () => {
          this.syncSortSelects();

          if (this.isDesktop()) {
            document.documentElement.classList.remove("facets-drawer-open");
            document.body.classList.remove("facets-drawer-open");

            if (!this.panel?.classList.contains("is-open")) {
              this.backdrop?.classList.remove("is-visible");
            }

            return;
          }

          if (this.inline?.classList.contains("is-open") && this.backdrop) {
            this.backdrop.classList.add("is-visible");
            document.documentElement.classList.add("facets-drawer-open");
            document.body.classList.add("facets-drawer-open");
          }
        };
        this.mediaQuery.addEventListener("change", this.handleMediaChange);

        this.querySelectorAll("[data-size-filter]").forEach((details) => {
          details.addEventListener("toggle", () => {
            if (this.mediaQuery.matches) {
              details.open = true;
            }
          });
        });

        this.handleKeydown = this.handleKeydown.bind(this);
        this.handleDocumentClick = this.handleDocumentClick.bind(this);
        document.addEventListener("keydown", this.handleKeydown);
        document.addEventListener("click", this.handleDocumentClick);
      }

      disconnectedCallback() {
        document.removeEventListener("keydown", this.handleKeydown);
        document.removeEventListener("click", this.handleDocumentClick);

        if (this.mediaQuery && this.handleMediaChange) {
          this.mediaQuery.removeEventListener("change", this.handleMediaChange);
        }

        if (!document.querySelector("[data-facets-inline].is-open, [data-facets-panel].is-open")) {
          this.releasePageScroll();
        }
      }

      /**
       * Drop the drawer scroll lock. Replacing the grid removes an open drawer
       * without going through close(), which left the page unscrollable.
       *
       * @returns {void}
       */
      releasePageScroll() {
        document.documentElement.classList.remove("facets-drawer-open");
        document.body.classList.remove("facets-drawer-open");
        document.body.style.overflow = "";
      }

      toggle() {
        const isOpen = this.panel?.classList.contains("is-open") || this.inline?.classList.contains("is-open");

        if (isOpen) {
          this.close();
        } else {
          this.open();
        }
      }

      /**
       * @returns {boolean}
       */
      isDesktop() {
        return this.mediaQuery?.matches ?? window.matchMedia("(min-width: 768px)").matches;
      }

      open() {
        if (this.panel && this.backdrop) {
          this.panel.classList.add("is-open");
          this.backdrop.classList.add("is-visible");
          document.body.style.overflow = "hidden";
        }
        if (this.inline) {
          this.inline.classList.add("is-open");
        }

        if (this.inline && !this.isDesktop() && this.backdrop) {
          this.backdrop.classList.add("is-visible");
          document.documentElement.classList.add("facets-drawer-open");
          document.body.classList.add("facets-drawer-open");
        }

        this.syncToggleButtons(true);
      }

      close() {
        if (this.panel && this.backdrop) {
          this.panel.classList.remove("is-open");
          this.backdrop.classList.remove("is-visible");
        }
        if (this.inline) {
          this.inline.classList.remove("is-open");
        }

        if (!this.panel?.classList.contains("is-open")) {
          this.backdrop?.classList.remove("is-visible");
        }

        this.releasePageScroll();
        this.syncToggleButtons(false);
      }

      syncToggleButtons(isOpen) {
        document.querySelectorAll("facets-button").forEach((button) => {
          button.classList.toggle("is-open", isOpen);
          button.setAttribute("aria-expanded", String(isOpen));
        });
      }

      getCheckedKeys() {
        const keys = new Set();

        if (!this.form) {
          return keys;
        }

        this.form.querySelectorAll('input[type="checkbox"]:checked:not([data-kids-sizes-toggle])').forEach((input) => {
          keys.add(`${input.name}=${input.value}`);
        });

        return keys;
      }

      syncSortSelects() {
        const isDesktop = this.mediaQuery?.matches ?? window.matchMedia("(min-width: 768px)").matches;

        document.querySelectorAll("[data-desktop-sort] [data-sort-select]").forEach((el) => {
          el.disabled = !isDesktop;
        });

        this.querySelectorAll("[data-mobile-sort] [data-sort-select]").forEach((el) => {
          el.disabled = isDesktop;
        });
      }

      syncSortValue(value) {
        document.querySelectorAll("[data-sort-select]").forEach((select) => {
          if (select.value !== value) {
            select.value = value;
          }
        });
      }

      /**
       * Expand or collapse kids sizes on desktop.
       *
       * @param {HTMLElement} toggle
       * @returns {void}
       */
      toggleKidsSizes(toggle) {
        const sizeFilter = toggle.closest("[data-size-filter]")
          || this.querySelector("[data-size-filter]");

        if (!sizeFilter) {
          return;
        }

        const input = sizeFilter.querySelector("[data-kids-sizes-toggle]");
        const expanded = input ? !input.checked : !sizeFilter.classList.contains("is-kids-open");

        if (input) {
          input.checked = expanded;
        }

        sizeFilter.classList.toggle("is-kids-open", expanded);
        sizeFilter.open = true;
      }

      /**
       * Hide the kids toggle when no kids sizes are in stock.
       *
       * @returns {void}
       */
      syncKidsSizesToggle() {
        const sizeFilter = this.querySelector("[data-size-filter]");

        if (!sizeFilter) {
          return;
        }

        const toggle = sizeFilter.querySelector(".facets__kids-toggle");
        const input = sizeFilter.querySelector("[data-kids-sizes-toggle]");
        const kidsGroup = sizeFilter.querySelector("[data-kids-sizes]");

        if (!toggle || !kidsGroup) {
          return;
        }

        const hasAvailable = this.hasAvailableKidsSizes(sizeFilter);

        sizeFilter.classList.toggle("has-kids-sizes", hasAvailable);
        toggle.hidden = !hasAvailable;

        if (input) {
          input.hidden = !hasAvailable;
        }

        if (!hasAvailable) {
          sizeFilter.classList.remove("is-kids-open");

          if (input) {
            input.checked = false;
          }
        }
      }

      /**
       * @param {HTMLElement} sizeFilter
       * @returns {boolean}
       */
      hasAvailableKidsSizes(sizeFilter) {
        const kidsInputs = sizeFilter.querySelectorAll("[data-kids-sizes] .facets__size-input");

        return Array.from(kidsInputs).some((input) => !input.disabled);
      }

      /**
       * Uncheck a filter. Applied chips immediately refresh results.
       *
       * @param {string} name
       * @param {string} value
       * @returns {void}
       */
      removeFilter(name, value) {
        const input = this.findInput(name, value);

        if (!input) {
          return;
        }

        const wasApplied = this.initialChecked.has(`${input.name}=${input.value}`);

        input.checked = false;

        if (wasApplied) {
          this.apply({ closePanel: false });
          return;
        }

        input.dispatchEvent(new Event("change", { bubbles: true }));
      }

      handleSubmit(event) {
        event.preventDefault();
        this.apply();
      }

      /**
       * Apply current form filters and refresh the product grid.
       *
       * @param {{ closePanel?: boolean }} [options]
       * @returns {void}
       */
      apply({ closePanel = true } = {}) {
        if (!this.form) return;

        const newUrl = this.buildUrlFromForm();

        if (closePanel) {
          this.close();
        }

        this.renderPage(newUrl);
      }

      buildUrlFromForm() {
        const formData = new FormData(this.form);
        const params = this.buildSearchParams(formData);
        const url = new URL(window.location);
        return `${url.pathname}?${params.toString()}`;
      }

      buildSearchParams(formData = null) {
        const url = new URL(window.location);
        const params = new URLSearchParams();

        ["q", "type"].forEach((key) => {
          const value = url.searchParams.get(key);
          if (value) params.set(key, value);
        });

        if (formData) {
          for (const [key, value] of formData.entries()) {
            if (value) params.append(key, value);
          }
        }

        return params;
      }

      async renderPage(url) {
        const productGrid = document.querySelector("main-product-grid");
        if (!productGrid) return;

        const inlineWasOpen = Boolean(this.inline?.classList.contains("is-open"));
        const keepMobileDrawer = inlineWasOpen && !this.isDesktop();
        const openDetailClasses = inlineWasOpen
          ? [...this.inline.querySelectorAll("details[open]")].map((details) => details.className)
          : [];

        productGrid.classList.add("is-loading");

        try {
          const html = await this.fetchPageHtml(url);
          const newProductGrid = this.extractProductGrid(html);

          if (newProductGrid) {
            if (inlineWasOpen) {
              newProductGrid.querySelector("[data-facets-inline]")?.classList.add("is-open");
            }

            if (keepMobileDrawer) {
              newProductGrid.querySelector("[data-facets-backdrop]")?.classList.add("is-visible");
            }

            if (openDetailClasses.length > 0) {
              newProductGrid.querySelectorAll("[data-facets-inline] details").forEach((details) => {
                if (openDetailClasses.includes(details.className)) {
                  details.open = true;
                }
              });
            }

            this.releasePageScroll();
            productGrid.innerHTML = newProductGrid.innerHTML;
            window.history.pushState({ path: url }, "", url);

            if (keepMobileDrawer) {
              document.documentElement.classList.add("facets-drawer-open");
              document.body.classList.add("facets-drawer-open");
              document.querySelectorAll("facets-button").forEach((button) => {
                button.classList.add("is-open");
                button.setAttribute("aria-expanded", "true");
              });
            }

            window.dispatchEvent(
                new CustomEvent("product-grid:updated", {
                  detail: { url, productGrid },
                })
            );
          }
        } catch (error) {
          console.error("Error fetching products:", error);
          window.location.href = url;
        } finally {
          productGrid.classList.remove("is-loading");
        }
      }

      async fetchPageHtml(url) {
        const response = await fetch(`${url}&section_id=${this.getSectionId()}`);

        if (!response.ok) {
          throw new Error(`Failed to fetch: ${response.status}`);
        }

        return response.text();
      }

      extractProductGrid(html) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        return doc.querySelector("main-product-grid");
      }

      getSectionId() {
        const productGrid = document.querySelector("[data-id]");
        return productGrid ? productGrid.dataset.id : "";
      }

      clear() {
        if (!this.form) return;

        this.resetFormInputs();

        const url = new URL(window.location);
        const params = this.buildSearchParams();
        const newUrl = `${url.pathname}?${params.toString()}`;

        this.close();
        this.renderPage(newUrl);
      }

      resetFormInputs() {
        this.form.querySelectorAll("input, select").forEach((input) => {
          if (input.type === "checkbox" || input.type === "radio") {
            input.checked = false;
          } else {
            input.value = "";
          }
        });
      }

      handleKeydown(event) {
        if (event.key !== "Escape") {
          return;
        }

        if (this.querySelector(".facets__filter-info-wrap.is-open")) {
          this.closeFitTooltips();
          return;
        }

        if (this.panel?.classList.contains("is-open") || this.inline?.classList.contains("is-open")) {
          this.close();
        }
      }

      /**
       * Close Fit info tooltips when clicking outside them.
       * @param {MouseEvent} event
       */
      handleDocumentClick(event) {
        if (event.target.closest("[data-facets-tooltip-wrap]")) {
          return;
        }

        this.closeFitTooltips();
      }

      /**
       * Toggle the Fit info tooltip. Hover also reveals it via CSS.
       * @param {HTMLElement} trigger
       */
      toggleFitTooltip(trigger) {
        const wrap = trigger.closest("[data-facets-tooltip-wrap]");

        if (!wrap) {
          return;
        }

        const willOpen = !wrap.classList.contains("is-open");

        this.closeFitTooltips();

        if (!willOpen) {
          return;
        }

        wrap.classList.add("is-open");
        trigger.setAttribute("aria-expanded", "true");
      }

      closeFitTooltips() {
        this.querySelectorAll(".facets__filter-info-wrap.is-open").forEach((wrap) => {
          wrap.classList.remove("is-open");

          const trigger = wrap.querySelector("[data-facets-tooltip]");

          if (trigger) {
            trigger.setAttribute("aria-expanded", "false");
          }
        });
      }

      setActiveFiltersFromUrl() {
        if (!this.form) return;

        const params = new URL(window.location).searchParams;

        params.forEach((value, key) => {
          const input = this.findInput(key, value);

          if (input) {
            if (input.type === "checkbox" || input.type === "radio") {
              input.checked = true;
            } else {
              input.value = value;
            }

            return;
          }

          this.setInputValue(key, value);
        });
      }

      setInputValue(name, value) {
        const input = this.form.querySelector(`[name="${CSS.escape(name)}"]`);
        if (!input) return;

        if (input.tagName === "SELECT") {
          input.value = value;
        } else if (input.type === "checkbox" || input.type === "radio") {
          if (input.value === value) input.checked = true;
        } else {
          input.value = value;
        }
      }

      /**
       * @param {number|string} count
       * @returns {void}
       */
      updateResultsCount(count) {
        const applyCount = this.form?.querySelector("[data-facets-apply-count]") || this.resultsCount;
        const safeCount = Number.isFinite(Number(count)) ? Number(count) : 0;

        if (applyCount) {
          applyCount.textContent = String(safeCount);
          applyCount.dataset.count = String(safeCount);
        }

        const applyButton = this.form?.querySelector(".facets__apply");
        const mobileLabel = this.form?.querySelector("[data-facets-apply-label]");

        if (!applyButton || !mobileLabel) {
          return;
        }

        const template = safeCount === 1 ? applyButton.dataset.showOne : applyButton.dataset.showOther;

        if (!template) {
          return;
        }

        mobileLabel.textContent = template.replace("__COUNT__", String(safeCount));
      }

      /**
       * Show how many values are selected in each filter group.
       *
       * @returns {void}
       */
      updateFilterCounts() {
        this.querySelectorAll(".facets__filter").forEach((filter) => {
          const countEl = filter.querySelector("[data-filter-count]");

          if (!countEl) {
            return;
          }

          const checked = filter.querySelectorAll('input[type="checkbox"]:checked:not([data-kids-sizes-toggle])').length;

          countEl.classList.toggle("is-active", checked > 0);
          countEl.textContent = checked > 0 ? `(${checked})` : "";
        });
      }

      handleFilterChange(event) {
        this.clearPendingUpdate();
        this.updateSelectionUI();

        this.pendingUpdate = setTimeout(() => {
          this.updateAvailability();
        }, this.DEBOUNCE_DELAY);
      }

      updateSelectionUI() {
        const checkedCount = this.form
          ? this.form.querySelectorAll('input[type="checkbox"]:checked:not([data-kids-sizes-toggle])').length
          : 0;

        if (this.inline) {
          this.inline.classList.toggle("is-filled", checkedCount > 0);
        }

        this.updateBadge(checkedCount);
        this.updateChanges();
        this.updateActiveChips();
        this.updateFilterCounts();
      }

      updateBadge(count) {
        document.querySelectorAll("[data-facets-badge]").forEach((badge) => {
          badge.dataset.count = String(count);
          badge.textContent = count > 0 ? String(count) : "";
        });
      }

      updateChanges() {
        if (!this.changesEl) {
          return;
        }

        const current = this.getCheckedKeys();
        let changes = 0;

        current.forEach((key) => {
          if (!this.initialChecked.has(key)) {
            changes += 1;
          }
        });

        this.initialChecked.forEach((key) => {
          if (!current.has(key)) {
            changes += 1;
          }
        });

        if (changes === 0) {
          this.changesEl.hidden = true;
          this.changesEl.textContent = "";
          return;
        }

        const template = changes === 1
          ? this.changesEl.dataset.one
          : this.changesEl.dataset.other;

        this.changesEl.hidden = false;
        this.changesEl.textContent = (template || "").replace("__COUNT__", String(changes));
      }

      updateActiveChips() {
        if (!this.activeEl || !this.form) {
          return;
        }

        const clearButton = this.activeEl.querySelector("[data-facets-clear]");
        const removeIcon = this.querySelector(".facets__chip-remove-icon")?.innerHTML
          || this.activeEl.querySelector(".facets__chip-remove")?.innerHTML
          || "×";
        const sizeTitle = this.querySelector(".facets__size-heading, .facets__filter__size .facets__filter-title")
          ?.textContent
          .trim();

        this.activeEl.querySelectorAll(".facets__chip").forEach((chip) => chip.remove());

        this.form.querySelectorAll('input[type="checkbox"]:checked:not([data-kids-sizes-toggle])').forEach((input) => {
          const option = input.closest(".facets__size-option, .facets__color-option, .facets__option");
          const label = option
            ?.querySelector(".facets__size-label, .facets__color-label, .facets__option-label")
            ?.textContent
            .trim();

          if (!label) {
            return;
          }

          const isSize = option?.classList.contains("facets__size-option");
          const button = document.createElement("button");
          const labelSpan = document.createElement("span");
          const removeSpan = document.createElement("span");

          labelSpan.className = "facets__chip-label";
          labelSpan.textContent = isSize && sizeTitle ? `${sizeTitle} ${label}` : label;
          removeSpan.className = "facets__chip-remove";
          removeSpan.setAttribute("aria-hidden", "true");
          removeSpan.innerHTML = removeIcon;

          button.type = "button";
          button.className = "facets__chip";
          button.dataset.facetsRemove = "";
          button.dataset.name = input.name;
          button.dataset.value = input.value;
          button.append(labelSpan, removeSpan);

          if (clearButton) {
            this.activeEl.insertBefore(button, clearButton);
          } else {
            this.activeEl.appendChild(button);
          }
        });
      }

      clearPendingUpdate() {
        if (this.pendingUpdate) {
          clearTimeout(this.pendingUpdate);
          this.pendingUpdate = null;
        }
      }

      showLoadingState() {
        if (this.loadingOverlay) {
          this.loadingOverlay.classList.add("is-visible");
        }
      }

      hideLoadingState() {
        if (this.loadingOverlay) {
          this.loadingOverlay.classList.remove("is-visible");
        }
      }

      async updateAvailability() {
        if (this.isUpdating || !this.form) return;

        this.isUpdating = true;
        this.showLoadingState();

        try {
          const formData = new FormData(this.form);
          const params = this.buildSearchParams(formData);
          const cacheKey = params.toString();

          if (this.availabilityCache.has(cacheKey)) {
            this.applyAvailabilityUpdates(this.availabilityCache.get(cacheKey));
            return;
          }

          const availabilityData = await this.fetchAvailabilityData(params);

          if (availabilityData) {
            this.cacheAvailabilityData(cacheKey, availabilityData);
            this.applyAvailabilityUpdates(availabilityData);
          }
        } catch (error) {
          console.error("Error updating filter availability:", error);
        } finally {
          this.isUpdating = false;
          this.hideLoadingState();
        }
      }

      async fetchAvailabilityData(params) {
        const url = new URL(window.location);
        const fetchUrl = `${url.pathname}?${params.toString()}&section_id=${this.getSectionId()}`;

        const response = await fetch(fetchUrl);

        if (!response.ok) {
          throw new Error("Failed to fetch filter updates");
        }

        const html = await response.text();
        return this.parseAvailabilityFromHtml(html);
      }

      parseAvailabilityFromHtml(html) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        const newFacets = doc.querySelector("custom-facets");

        return newFacets ? this.extractAvailabilityData(newFacets) : null;
      }

      cacheAvailabilityData(key, data) {
        this.availabilityCache.set(key, data);

        if (this.availabilityCache.size > this.MAX_CACHE_SIZE) {
          const firstKey = this.availabilityCache.keys().next().value;
          this.availabilityCache.delete(firstKey);
        }
      }

      extractAvailabilityData(facetsElement) {
        return {
          inputs: this.extractInputsData(facetsElement),
          resultsCount: this.extractResultsCount(facetsElement),
        };
      }

      extractInputsData(facetsElement) {
        const inputs = facetsElement.querySelectorAll(CustomFacets.SELECTORS.FILTER_INPUTS);
        const inputsData = [];

        inputs.forEach((input) => {
          const option = input.closest(CustomFacets.SELECTORS.FILTER_OPTIONS);
          const countElement = option?.querySelector(CustomFacets.SELECTORS.COUNT_ELEMENTS);

          inputsData.push({
            name: input.name,
            value: input.value,
            disabled: input.disabled,
            count: this.parseCount(countElement?.textContent),
          });
        });

        return inputsData;
      }

      extractResultsCount(facetsElement) {
        const resultsElement = facetsElement.querySelector("[data-facets-apply-count], [data-facets-results]");
        return this.parseCount(resultsElement?.dataset?.count || resultsElement?.textContent);
      }

      parseCount(text) {
        if (!text) return 0;
        const match = text.match(/\d+/);
        return match ? parseInt(match[0]) : 0;
      }

      applyAvailabilityUpdates(data) {
        if (!data || !this.form) return;

        const updates = this.prepareAvailabilityUpdates(data);

        requestAnimationFrame(() => {
          updates.forEach((update) => update());

          if (data.resultsCount !== undefined) {
            this.updateResultsCount(data.resultsCount);
          }

          this.syncKidsSizesToggle();
        });
      }

      prepareAvailabilityUpdates(data) {
        const updates = [];
        const hideUnavailable = !(this.mediaQuery?.matches);
        const options = this.form
          ? this.form.querySelectorAll(CustomFacets.SELECTORS.FILTER_OPTIONS)
          : [];

        options.forEach((el) => {
          el.classList.toggle("is-unavailable", hideUnavailable);
        });

        data.inputs.forEach((inputData) => {
          const input = this.findInput(inputData.name, inputData.value);

          if (!input) {
            return;
          }

          const option = input.closest(CustomFacets.SELECTORS.FILTER_OPTIONS);
          const countElement = option?.querySelector(CustomFacets.SELECTORS.COUNT_ELEMENTS);

          if (option) {
            option.classList.remove("is-unavailable");
          }

          if (input.disabled !== inputData.disabled) {
            updates.push(() => {
              input.disabled = inputData.disabled;
            });
          }

          if (countElement && inputData.count !== undefined) {
            updates.push(() => {
              countElement.textContent = `${inputData.count}`;
            });
          }
        });

        return updates;
      }

      /**
       * Find a checkbox by name and value without CSS selector escaping issues.
       *
       * @param {string} name
       * @param {string} value
       * @returns {HTMLInputElement|null}
       */
      findInput(name, value) {
        if (!this.form || name == null || value == null) {
          return null;
        }

        const inputs = this.form.querySelectorAll('input[type="checkbox"]');

        for (const input of inputs) {
          if (input.name === name && input.value === value) {
            return input;
          }
        }

        return null;
      }
    }

    customElements.define("custom-facets", CustomFacets);
  }
})();
